import {
  useCallback,
  useMemo,
  type MouseEvent,
  type ReactElement,
} from 'react';
import type { Control } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import type {
  EnforceDisabledReason,
  PolicyModeType,
  PolicySetting,
} from '@kirin/api-client/contracts/policies';
import { PolicySettingField } from '@/components/policylist/components/PolicySettingField';
import { EnforceAvailabilityContext } from '@/components/policylist/components/enforceAvailability';
import { ParentPolicyModeContext } from '@/components/policylist/components/parentPolicyMode';
import type { PolicySettingsFormData } from '@/components/policylist/types';
import type { AlertSubPolicyExtra } from './alertSubPolicyExtras';

interface RelatedPolicySettingsProps {
  policyKey: string;
  policyName: string;
  /** The alert sub-policy's related settings, in display order. */
  settings: PolicySetting[];
  control: Control<PolicySettingsFormData>;
  shouldHideSetting: (settingKey: string) => boolean;
  readOnly: boolean;
  isSupportReadOnly: boolean;
  isDirty: boolean;
  /** The dialog's pending Off/Monitor/Enforce mode (gates nested sub-modes). */
  parentMode: PolicyModeType;
  supportsEnforce: boolean;
  enforceDisabledReason?: EnforceDisabledReason;
  /** Unsaved edits anywhere in the popup (mode, sub-policy or these rows). */
  hasUnsavedChanges: boolean;
  /** Asks to discard the popup's unsaved edits, then runs `proceed`. */
  confirmDiscard: (proceed: () => void) => void;
  /** Non-setting rows (e.g. the built-in catalog link), after the settings. */
  extras?: readonly AlertSubPolicyExtra[];
}

/**
 * KIR-4929: the settings the Policies page shows next to an alert's sub-policy
 * (e.g. "Secret Detection Rules" beside "Secret Rules"), rendered with the
 * Policies page's own `PolicySettingField` so each row looks and behaves the
 * same in the alert "Edit Policy" popup. Which keys appear is decided by
 * `ALERT_SUB_POLICY_RELATED_SETTINGS`.
 */
export function RelatedPolicySettings({
  policyKey,
  policyName,
  settings,
  control,
  shouldHideSetting,
  readOnly,
  isSupportReadOnly,
  isDirty,
  parentMode,
  supportsEnforce,
  enforceDisabledReason,
  hasUnsavedChanges,
  confirmDiscard,
  extras = [],
}: RelatedPolicySettingsProps): ReactElement {
  const navigate = useNavigate();
  const enforceAvailability = useMemo(
    () => ({ supportsEnforce, enforceDisabledReason }),
    [supportsEnforce, enforceDisabledReason]
  );
  const parentPolicyGate = useMemo(
    () => ({ mode: parentMode, name: policyName }),
    [parentMode, policyName]
  );

  // Rows such as GitleaksRulesLink link to a Policies sub-page. Nothing blocks
  // route changes app-wide, so a click while the popup is dirty would drop its
  // edits silently — route it through the popup's discard prompt instead.
  const handleClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (!hasUnsavedChanges || !(event.target instanceof Element)) return;
      // A modified click opens a new tab/window and leaves the popup intact.
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const href = event.target.closest('a[href]')?.getAttribute('href');
      if (!href) return;
      event.preventDefault();
      confirmDiscard(() => {
        navigate(href);
      });
    },
    [hasUnsavedChanges, confirmDiscard, navigate]
  );

  return (
    <EnforceAvailabilityContext.Provider value={enforceAvailability}>
      <ParentPolicyModeContext.Provider value={parentPolicyGate}>
        <div
          className="space-y-4"
          data-testid="related-policy-settings"
          onClickCapture={handleClickCapture}
        >
          {settings.map((setting, index) => {
            const hidden = shouldHideSetting(setting.key);
            // Group heading as on the Policies page; a GitleaksRulesLink row
            // has no label of its own and relies on it. Compare against the
            // previous *visible* row, so a hidden row never swallows it.
            const previousVisible = settings
              .slice(0, index)
              .filter(s => !shouldHideSetting(s.key))
              .at(-1);
            const showGroupHeading =
              !hidden &&
              !!setting.group &&
              setting.group !== previousVisible?.group;
            return (
              <div key={setting.key}>
                {showGroupHeading && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      {setting.group}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                )}
                <PolicySettingField
                  setting={setting}
                  policyKey={policyKey}
                  control={control}
                  readOnly={readOnly}
                  isSupportReadOnly={isSupportReadOnly}
                  hidden={hidden}
                  isDirty={isDirty}
                />
              </div>
            );
          })}
          {extras.map(extra => (
            <div key={extra.id}>{extra.render()}</div>
          ))}
        </div>
      </ParentPolicyModeContext.Provider>
    </EnforceAvailabilityContext.Provider>
  );
}
